/**
 * Placeholder origin, only used to resolve `returnTo` values with the browser's own
 * URL parser. The `.invalid` TLD is reserved (RFC 2606), so it can't be a real host
 */
const RETURN_TO_PLACEHOLDER_ORIGIN = "https://return-to.invalid";

/**
 * How many rounds of percent-decoding to apply to the path part of a `returnTo`
 * value when looking for encoded tricks like `/%2F%2Fevil.com`
 */
const RETURN_TO_MAX_DECODE_ROUNDS = 3;

/**
 * ASCII control characters (including tab, CR, and LF), which browsers silently
 * strip out of URLs, e.g. `/\t/evil.com` becomes `//evil.com`
 */
const hasControlCharacter = (value: string): boolean =>
  [...value].some(char => {
    const code = char.codePointAt(0) ?? 0;
    // 0-31 is the C0 control range, 127 is DEL
    return code <= 31 || code === 127;
  });

/**
 * `true` if `path` starts with exactly one `/` (not `//`, which is protocol-relative,
 * or `/\`, which browsers treat as `//`), has no backslashes or control characters,
 * and resolves to the same origin it started from
 */
const isSameAppPath = (path: string): boolean => {
  if (
    !path.startsWith("/") ||
    path.startsWith("//") ||
    path.includes("\\") ||
    hasControlCharacter(path)
  ) {
    return false;
  }
  try {
    return (
      new URL(path, RETURN_TO_PLACEHOLDER_ORIGIN).origin ===
      RETURN_TO_PLACEHOLDER_ORIGIN
    );
  } catch {
    return false;
  }
};

/**
 * Returns `returnTo` unchanged if it's a relative path within this app, otherwise
 * `undefined`, so callers fall back to their default route.
 *
 * `returnTo` comes straight from the URL (see `useReturnTo`), and React Router v6's
 * `<Link>` renders absolute (`https://...`) and protocol-relative (`//...`) values as
 * plain external links, so an unchecked value is an open redirect.
 *
 * The path part (before any `?` or `#`) is also percent-decoded a few times and
 * re-checked, to catch encoded variants like `/%2F%2Fevil.com`. The query string and
 * hash are left alone: they can't change where the link goes, and legit values can
 * contain encoded characters
 */
export const getSafeReturnTo = (returnTo: unknown): string | undefined => {
  if (typeof returnTo !== "string" || !isSameAppPath(returnTo)) {
    return undefined;
  }

  let path = returnTo.split(/[?#]/)[0];
  for (let round = 0; round < RETURN_TO_MAX_DECODE_ROUNDS; round++) {
    let decodedPath: string;
    try {
      decodedPath = decodeURIComponent(path);
    } catch {
      // Malformed percent-encoding
      return undefined;
    }
    if (decodedPath === path) {
      return returnTo;
    }
    if (!isSameAppPath(decodedPath)) {
      return undefined;
    }
    path = decodedPath;
  }

  // Still changing after several rounds of decoding, which no real route needs
  return undefined;
};
